import { createContext, type ContextType, useContext } from "react";

export const LocalesContext = createContext({
  locale: "en-US",
  general: {
    save: "Save",
    saveAndClose: "Save and close",
    reset: "Reset",
  },
  confirmSubmit: {
    title: "Do you wish to proceed?",
    confirm: "Confirm",
    cancel: "Cancel",
  },
  confirmReset: {
    title: "Reset all changes?",
    description: "Are you sure you want to reset? All data will be lost.",
    confirm: "Reset",
    cancel: "Cancel",
  },
  advertiserPicker: {
    placeholder: "Search...",
    noResults: "No results found",
    noSelection: "No selection",
    favoritesFilter: "Favorites",
    favoritesSection: "Favorites",
    addToFavorites: "Add to favorites",
    removeFromFavorites: "Remove from favorites",
    navigateHint: "to navigate",
    selectHint: "to select",
    closeHint: "to close",
    toggleHint: "to toggle",
    toggleTooltip: (shortcut: string) => `Use ${shortcut} to toggle`,
    accountInfo: (accountId: string, clientId: string) =>
      `Advertiser ${accountId} · Agency ${clientId}`,
  },
  errorPage: {
    title: "Internal Error",
    description: "Sorry, we encountered an unexpected error.",
    contactSupport: "Feel free to contact the support if the problem persists.",
    goBack: "Go Back",
    goHome: "Go to Homepage",
  },
  notFound: {
    title: "Not found",
    clearFilters: "Clear filters",
    description: "The page you are looking for does not exist.",
  },
  notAllowed: {
    title: "Not allowed",
    description: "You are not allowed to see this page.",
    requestId: (id: string) => `Request ID: ${id}`,
    goBack: "Go Back",
    goHome: "Go to Homepage",
  },
  select: {
    selectAll: "Select all",
    removeAll: "Remove all",
    copy: "Copy",
    copyTooltip: "Copy selection to clipboard",
    copySuccess: "Selection copied to clipboard",
    paste: "Paste from clipboard",
    pasteTooltip: "Replace current selection with values from clipboard",
    pasteErrorTitle: "Paste error",
    pasteErrorDescription:
      'The items you tried to paste were not in the correct format. Make sure to use the format "id_1;id_2"',
  },
  autocomplete: {
    nextPage: "Load next page",
  },
  commandMenu: {
    placeholder: "Search...",
    empty: "No results found",
    navigateHint: "to navigate",
    selectHint: "to select",
    closeHint: "to close",
    toggleHint: "to toggle",
  },
  accountDropdown: {
    searchPlaceholder: "Search...",
  },
  headerAccountPicker: {
    searchPlaceholder: "Search...",
    selectAccount: "Select account",
  },
  pagination: {
    pageSizeLabel: "Display",
    pageSizeOptions: (size: number) => `${size} lines`,
    firstPage: "First page",
    previousPage: "Previous page",
    nextPage: "Next page",
    lastPage: "Last page",
  },
  filters: {
    searchPlaceholder: "Search",
  },
  table: {
    arrangeColumns: "Arrange your columns",
    expandRow: "Show details",
    collapseRow: "Hide details",
    expandAllRows: "Show all details",
    collapseAllRows: "Hide all details",
  },
  sidebar: {
    languageSelector: {
      title: "Change language",
      description: "Select your language",
      cancel: "Cancel",
      confirm: "Confirm",
      errorTitle: "Error changing language",
      errorDescription:
        "Something went wrong, please try again later, if the problem persists, please contact support.",
    },
    logout: "Disconnect",
    admin: "Administration",
    editTranslations: "Edit translations",
    openMenu: "Open menu",
  },
  dateRangePicker: {
    reset: "Reset",
    from: "From",
    to: "to",
    rangePlaceholder: "Filter by date",
    thisWeek: "This week",
    lastWeek: "Last week",
    thisMonth: "This month",
    lastMonth: "Last month",
    thisQuarter: "This quarter",
    lastQuarter: "Last quarter",
    thisYear: "This year",
    lastYear: "Last year",
    last3Days: "Last 3 days",
    last7Days: "Last 7 days",
    last14Days: "Last 14 days",
    last30Days: "Last 30 days",
    last90Days: "Last 90 days",
    today: "Today",
    yesterday: "Yesterday",
  },
  dateTimePicker: {
    ok: "OK",
  },
  moby: {
    title: "Moby",
    send: "Send",
    askAQuestion: "Ask a question here",
    copyMessage: "Copy message",
    copiedMessage: "Copied",
    resetRequest: "Reset request",
    resetRequestDescription:
      "Do you really want to erase and reset your request?",
    cancel: "Cancel",
    reset: "Reset",
    happyWithThisConv: "Happy with this conversation?",
    description: "The AI chat companion of Mobsuccess",
    openChat: "Open Moby chat",
    downloadFile: "Download file",
    downloadingFile: "Downloading...",
    downloadFileError: "Download failed",
  },
  audioPlayer: {
    play: "Play",
    pause: "Pause",
    loading: "Loading audio",
    error: "Error loading audio",
  },
  dataEditor: {
    upload: {
      error: "Error uploading file",
    },
    views: {
      table: "Table View",
      csv: "CSV View",
    },
    csv: {
      apply: "Apply",
      revert: "Revert",
      format: "Format",
      upToDate: "Up to date",
      unsavedChanges: "Unsaved changes",
    },
    records(count: number) {
      return `${count} record${count > 1 ? "s" : ""}`;
    },
  },
  field: {
    optional: "Optional",
    charCount(count: number, limit: number) {
      return `${count}/${limit} characters`;
    },
  },
  fileUpload: {
    rejected: {
      title(name: string) {
        return `File ${name}`;
      },
      reason(reasons: string[]) {
        return `Rejected for the following reasons: ${reasons.join(", ")}`;
      },
    },
  },
  calendar: {
    today: "Today",
    month: "Month",
    twoMonths: "2 months",
    year: "Year",
    weekLetter: "W",
    others: "others",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    toggleAllItems: "Toggle all items",
  },
  chat: {
    labels: {
      favoriteButton: "Favorite",
      selectedTags: "Selected tags",
      availableTags: "Available tags",
      backToConversation: "Back to conversation",
      favoriteCount(count: number) {
        return `${count} favorite${count > 1 ? "s" : ""}`;
      },
    },
    placeholders: {
      search: "Search messages...",
    },
    loading: "Loading...",
    emptyTitle: "No messages",
    emptySubtitle: "No messages match your filters",
    send: "Send",
  },
});

export function useLocales(): ContextType<typeof LocalesContext> {
  return useContext(LocalesContext);
}
